/**
 * WorkspaceShell — Main container for Workspace Shell V1
 * 
 * Provides the modern workspace layout with:
 * - Consolidated top navigation (primary + feature buttons)
 * - Main workspace area (children)
 * - Contextual right panel (ContextDrawer)
 */
import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ContextDrawer } from './ContextDrawer';
import { Toaster } from '../ui/sonner';
import { workspaceAPI, WorkspaceBootstrap, notificationsAPI } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { Settings, LogOut, Menu, X, Shield } from 'lucide-react';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLitpulseChromeHeight } from '../../hooks/useLitpulseChromeHeight';
import { LITPULSE_DIGEST_SLOT_ID } from '../../constants/litpulseChrome';
import { FeatureNavBar } from '../shared/FeatureNavBar';

// Feature navigation — shared config (F11)

interface WorkspaceContextValue {
  bootstrap: WorkspaceBootstrap | null;
  loading: boolean;
  refreshBootstrap: () => Promise<void>;
  selectedArticle: any | null;
  setSelectedArticle: (article: any | null) => void;
  contextOpen: boolean;
  setContextOpen: (open: boolean) => void;
}

const WorkspaceContext = React.createContext<WorkspaceContextValue>({
  bootstrap: null,
  loading: true,
  refreshBootstrap: async () => {},
  selectedArticle: null,
  setSelectedArticle: () => {},
  contextOpen: false,
  setContextOpen: () => {},
});

export const useWorkspace = () => React.useContext(WorkspaceContext);

export const WorkspaceShell: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const navV2Enabled = useFeatureFlag('enable_navigation_v2');
  const chromeRef = useLitpulseChromeHeight<HTMLDivElement>();
  
  // Bootstrap state
  const [bootstrap, setBootstrap] = React.useState<WorkspaceBootstrap | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);
  
  // Context panel state
  const [selectedArticle, setSelectedArticle] = React.useState<any | null>(null);
  const [contextOpen, setContextOpen] = React.useState(false);

  // Fetch bootstrap data on mount
  const fetchBootstrap = React.useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const data = await workspaceAPI.getBootstrap();
      setBootstrap(data);
    } catch (error) {
      console.error('Failed to fetch workspace bootstrap:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  React.useEffect(() => {
    fetchBootstrap();
  }, [fetchBootstrap]);

  React.useEffect(() => {
    if (!user) return;

    let interval: ReturnType<typeof setInterval> | null = null;

    const fetchUnreadCount = async () => {
      try {
        const response = await notificationsAPI.getUnreadCount();
        setUnreadCount(response.unread_count);
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };

    const startPolling = () => {
      if (interval) return;
      fetchUnreadCount();
      interval = setInterval(fetchUnreadCount, 30000);
    };

    const stopPolling = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) stopPolling();
      else startPolling();
    };

    if (!document.hidden) startPolling();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user]);

  // Context value
  const contextValue: WorkspaceContextValue = {
    bootstrap,
    loading,
    refreshBootstrap: fetchBootstrap,
    selectedArticle,
    setSelectedArticle,
    contextOpen,
    setContextOpen,
  };

  // Handle article selection - open context drawer
  const handleOpenArticle = (article: any) => {
    setSelectedArticle(article);
    setContextOpen(true);
  };

  return (
    <WorkspaceContext.Provider value={contextValue}>
      <div 
        className="h-dvh max-h-dvh w-full flex flex-col overflow-hidden bg-gradient-to-b from-sky-50/30 via-slate-50 to-white"
        data-testid="workspace-shell"
      >
        {/* Top chrome — sticky (nav + feature tabs) */}
        <div ref={chromeRef} className="sticky top-0 z-50 bg-white shadow-sm">
        <header className="border-b border-slate-200">
          {/* Primary nav row */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14">
              {/* Logo */}
              <button 
                onClick={() => navigate('/library')}
                className="flex items-center gap-2 text-xl font-bold text-slate-800 hover:text-sky-600 transition-colors"
              >
                <span className="text-2xl">💊</span>
                <span>LitCurrent</span>
              </button>

              {/* Desktop nav */}
              <div className="hidden md:flex items-center gap-2">
                <button
                  onClick={() => navigate('/library')}
                  className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
                >
                  Home
                </button>
                <button
                  onClick={() => navigate('/community')}
                  className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
                >
                  LitClub
                </button>
                <button
                  onClick={() => navigate('/inbox')}
                  className="relative px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
                >
                  Inbox
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-4 min-w-[16px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => navigate('/preferences')}
                  className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
                >
                  Preferences
                </button>
                {(user as any)?.email?.toLowerCase() === 'info@scienthesis.ai' && (
                  <button
                    onClick={() => navigate('/beta-admin')}
                    className="px-3 py-1.5 text-sm text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md transition-colors font-medium"
                    title="Admin Dashboard"
                  >
                    <Shield className="h-4 w-4 inline mr-1" />Admin
                  </button>
                )}
                <button
                  onClick={() => navigate('/settings')}
                  className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                  title="Settings"
                >
                  <Settings className="h-5 w-5" />
                </button>
                <button
                  onClick={logout}
                  className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Logout"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-slate-200 bg-white py-2 px-4">
              <div className="flex flex-col gap-1">
                <button onClick={() => { navigate('/library'); setMobileMenuOpen(false); }} className="text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Home</button>
                <button onClick={() => { navigate('/inbox'); setMobileMenuOpen(false); }} className="text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md flex items-center justify-between">
                  Inbox
                  {unreadCount > 0 && (
                    <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                <button onClick={() => { navigate('/preferences'); setMobileMenuOpen(false); }} className="text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Preferences</button>
                <button onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }} className="text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Settings</button>
                <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md">Logout</button>
              </div>
            </div>
          )}
        </header>

        {/* Secondary Navigation - Feature Buttons */}
        <FeatureNavBar />
        <div id={LITPULSE_DIGEST_SLOT_ID} />
        </div>

        {/* Main workspace area — fills remaining viewport below chrome */}
        <section className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden">
          <main className="flex-1 min-h-0 overflow-y-auto flex flex-col">
            {children || <Outlet />}
          </main>
        </section>

        {/* Context drawer (right panel) - for article details */}
        <ContextDrawer
          open={contextOpen}
          onOpenChange={setContextOpen}
          article={selectedArticle}
        />

        {/* Toast notifications */}
        <Toaster />
      </div>
    </WorkspaceContext.Provider>
  );
};

export default WorkspaceShell;
